import { Collapse, Intent } from "@blueprintjs/core";
import {
  CREATE_SYNC_REPOSITORY_HEX_VERSION_LINK,
  DELETE_SYNC_REPOSITORY_HEX_VERSION_LINK,
  DOCS_LINKS,
  OrgRole,
  SpecialVersionType,
  SyncRepositoryHexVersionLinkId,
  UPDATE_SYNC_REPOSITORY_HEX_VERSION_LINK,
  VcsConnectionType,
  assertNever,
  vcsTypeToDisplayName,
} from "@hex/common";
import { castDraft } from "immer";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router";
import styled, { css } from "styled-components";
import { v4 as uuid } from "uuid";

import {
  HexButton,
  HexMenu,
  HexMenuItem,
  HexSwitch,
  HexTooltip,
} from "../../hex-components";
import { HexButtonWithDropdown } from "../../hex-components/HexButtonWithDropdown";
import { useHexVersionSelector } from "../../hex-version-multiplayer/state-hooks/hexVersionStateHooks";
import { useSyncRepositoryHexVersionLinksSelector } from "../../hex-version-multiplayer/state-hooks/syncRepositoryHexVersionLinkStateHooks";
import { useCurrentUser } from "../../hooks/me/useCurrentUser";
import { ORG_ID } from "../../orgs";
import { useDispatch } from "../../redux/hooks";
import { setImportWarnings } from "../../redux/slices/logicViewSlice";
import { Routes } from "../../route/routes";
import { useHexVersionAOContext } from "../../util/hexVersionAOContext";
import { useProjectContext } from "../../util/projectContext";
import { DocsLink } from "../common/DocsLink";
import { Form } from "../common/form/Form";
import { MinimalTable } from "../common/MinimalTable";
import { useToaster } from "../common/Toasts";
import { useFeatureGates } from "../feature-gate/FeatureGateContext";
import { FeatureGateToolTip } from "../feature-gate/FeatureGateToolTip";
import { GitPullIcon, RefreshIcon } from "../icons/CustomIcons";
import { SidebarHeader } from "../sidebar/SidebarHeader";
import { SidebarDivider } from "../sidebar/SidebarWrapper";
import { checkForWarnings } from "../upload/ImportProjectDialog";

import {
  SyncRepoAndWorkingBranch,
  renderVcsPackageTableCells,
} from "./OrgRepoList";
import {
  SyncRepoFragment,
  useGetOrgSyncReposQuery,
  usePullFromGitRepoMutation,
} from "./queries.generated";
import {
  RemoteFilePathFormData,
  SyncSettingsDialog,
} from "./SyncSettingDialog";
import { GenericError, PullError } from "./Toasts";

const tableColumns = [
  {
    id: "syncRepos-1",
    name: "",
  },
  {
    id: "syncRepos-2",
    name: "",
    css: css`
      flex: none;
    `,
  },
];

const Container = styled.div`
  flex: 1 0 auto;
  padding-bottom: 30px;
`;

const ButtonContainer = styled.div`
  margin: 10px 0;
  padding: 0 15px;
`;

const PullRequestButton = styled(HexButton)`
  height: 24px;
`;

export function generateCompareUrl({
  projectUrl,
  repo,
  workingBranch,
}: {
  projectUrl: string;
  repo: SyncRepoFragment;
  workingBranch: string;
}): string {
  const hexLink = encodeURIComponent(`[View project in Hex](${projectUrl})`);
  switch (repo.vcsConnectionType) {
    case VcsConnectionType.GITLAB_SYNC:
      return `https://gitlab.com/${repo.repoOwner}/${repo.repoName}/merge_requests/new?merge_request[source_branch]=${workingBranch}`;
    case VcsConnectionType.GITHUB:
    case VcsConnectionType.GITHUB_SYNC:
      return `https://github.com/${repo.repoOwner}/${repo.repoName}/compare/${workingBranch}?quick_pull=1&body=${hexLink}`;
    default:
      assertNever(repo.vcsConnectionType, repo.vcsConnectionType);
  }
}

export const ProjectGithubSyncPanel: React.ComponentType = React.memo(
  function ProjectGithubSyncPanel() {
    const { githubSync: enabled } = useFeatureGates();
    const { hexId, hexVersionId, projectEditable } = useProjectContext();
    const projectTitle = useHexVersionSelector({
      selector: (hexVersion) => hexVersion.title,
    });
    const defaultFilePath = useMemo(() => {
      const nameSlug = projectTitle
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_\-\s]/gi, "")
        .replace(/\s+/g, "-");
      return `${nameSlug}/${nameSlug}.yaml`;
    }, [projectTitle]);
    const formRef = useRef<Form<RemoteFilePathFormData> | null>(null);
    const toaster = useToaster();

    const [updateError, setUpdateError] = useState<string | undefined>(
      undefined,
    );
    const [fileExists, setFileExists] = useState<boolean>(false);
    const { data, error, loading } = useGetOrgSyncReposQuery({
      variables: { orgId: ORG_ID },
    });
    const noRepos = data?.orgSyncRepos.length === 0;
    const { dispatchAO: dispatchHexVersionAO } = useHexVersionAOContext();
    const syncRepoLinks = useSyncRepositoryHexVersionLinksSelector({
      selector: (state) => state,
    });
    const currentLinkId = Object.keys(
      syncRepoLinks,
    )[0] as SyncRepositoryHexVersionLinkId;

    const [formData, setFormData] = useState<RemoteFilePathFormData>({
      path: syncRepoLinks[currentLinkId]?.filePath ?? defaultFilePath,
    });
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);

    const onChangeForm = useCallback(
      (newFormData: RemoteFilePathFormData) => {
        setFormData(newFormData);
        setFileExists(false);
        setUpdateError(undefined);
      },
      [setFormData],
    );

    const currentRepoId = syncRepoLinks[currentLinkId]?.syncRepositoryId;
    const currentRepo = useMemo(
      () => data?.orgSyncRepos.find((r) => r.id === currentRepoId),
      [currentRepoId, data?.orgSyncRepos],
    );
    const [syncOn, setSyncOn] = useState<boolean>(!!currentLinkId);
    const [selectedRepo, setSelectedRepo] = useState<
      SyncRepoFragment | undefined
    >(currentRepo);

    const workingBranch = useMemo(
      () => syncRepoLinks[currentLinkId]?.branch ?? "",
      [currentLinkId, syncRepoLinks],
    );

    const pullRequestCallback = useCallback(() => {
      if (currentRepo) {
        const baseUrl = Routes.href(ORG_ID, true, Routes.LOGIC, {
          hexId,
          version: SpecialVersionType.DRAFT,
        });
        window.open(
          generateCompareUrl({
            repo: currentRepo,
            workingBranch,
            projectUrl: baseUrl,
          }),
        );
      }
    }, [currentRepo, hexId, workingBranch]);

    useEffect(() => {
      // Auto-opens the dialog for new sync repo links
      if (syncRepoLinks[currentLinkId]?.filePath === null) {
        setDialogOpen(true);
      }
      // Sets the selected repo once the current repo information is loaded
      if (selectedRepo === undefined && currentRepo !== undefined) {
        setSelectedRepo(currentRepo);
      }
    }, [currentLinkId, currentRepo, selectedRepo, syncRepoLinks]);

    const currentUser = useCurrentUser();
    const editorRoles: OrgRole[] = [OrgRole.ADMIN, OrgRole.EDITOR];
    const canEdit =
      (currentUser?.orgRole != null &&
        editorRoles.includes(currentUser.orgRole) &&
        projectEditable) ??
      false;

    const toggleSyncCallback = useCallback(
      async (evt: React.FormEvent<HTMLInputElement>) => {
        const setOn = evt.currentTarget.checked;
        if (setOn && data?.orgSyncRepos[0]) {
          setSyncOn(true);
          try {
            await dispatchHexVersionAO(
              CREATE_SYNC_REPOSITORY_HEX_VERSION_LINK.create({
                syncRepositoryHexVersionLinkId:
                  uuid() as SyncRepositoryHexVersionLinkId,
                syncRepositoryId: data?.orgSyncRepos[0].id,
                filePath: null,
              }),
            )[1];
          } catch (e) {
            setSyncOn(false);
            if (e instanceof Error) {
              toaster.show({
                message: <GenericError message={e.message} />,
                intent: Intent.DANGER,
              });
              console.error(e);
            }
          }
        } else {
          setSyncOn(false);
          dispatchHexVersionAO(
            DELETE_SYNC_REPOSITORY_HEX_VERSION_LINK.create(currentLinkId),
          );
        }
      },
      [currentLinkId, data?.orgSyncRepos, dispatchHexVersionAO, toaster],
    );

    const closeDialog = useCallback(() => {
      setDialogOpen(false);
      if (!syncRepoLinks[currentLinkId]?.filePath) {
        dispatchHexVersionAO(
          DELETE_SYNC_REPOSITORY_HEX_VERSION_LINK.create(currentLinkId),
        );
        setSyncOn(false);
      }
    }, [currentLinkId, dispatchHexVersionAO, syncRepoLinks]);

    const sameFile = formData.path === syncRepoLinks[currentLinkId]?.filePath;

    const [formLoading, setFormLoading] = useState(false);
    const submitForm = useCallback(async () => {
      if (formRef.current?.validate() && formData.path && selectedRepo) {
        setFormLoading(true);
        const oldPath = syncRepoLinks[currentLinkId]?.filePath;
        try {
          if (selectedRepo.id !== currentRepoId) {
            await dispatchHexVersionAO([
              DELETE_SYNC_REPOSITORY_HEX_VERSION_LINK.create(currentLinkId),
              CREATE_SYNC_REPOSITORY_HEX_VERSION_LINK.create({
                syncRepositoryHexVersionLinkId:
                  uuid() as SyncRepositoryHexVersionLinkId,
                syncRepositoryId: selectedRepo.id,
                filePath: formData.path,
              }),
            ])[1];
          } else {
            await dispatchHexVersionAO([
              UPDATE_SYNC_REPOSITORY_HEX_VERSION_LINK.create({
                filePath: formData.path,
                syncRepositoryHexVersionLinkId: currentLinkId,
                force: fileExists || sameFile,
              }),
            ])[1];
          }
          setUpdateError(undefined);
          setFileExists(false);
          setDialogOpen(false);
        } catch (err) {
          if (err instanceof Error) {
            setUpdateError(err.message);
            if (err.message.includes("already exists")) {
              setFileExists(true);
            }
            if (oldPath !== undefined) {
              // Because we caught the error, need to set this back to the old value so
              // the client has the correct info.
              dispatchHexVersionAO(
                UPDATE_SYNC_REPOSITORY_HEX_VERSION_LINK.create({
                  filePath: oldPath,
                  syncRepositoryHexVersionLinkId: currentLinkId,
                }),
              );
            }
          } else {
            setUpdateError("Internal server error");
            setFileExists(false);
          }
        }
        setFormLoading(false);
      }
    }, [
      currentLinkId,
      currentRepoId,
      dispatchHexVersionAO,
      fileExists,
      formData.path,
      sameFile,
      selectedRepo,
      syncRepoLinks,
    ]);

    let tooltipContent = "";
    if (error) {
      tooltipContent = "Error loading sync repositories";
    } else if (noRepos) {
      tooltipContent =
        "Unable to enable sync because there are no sync repositories configured. Contact an Admin to create one.";
    } else if (!canEdit) {
      tooltipContent = `Only members of ${
        currentUser?.org.displayName ?? "this workspace"
      } with edit access to this project can configure Git sync`;
    }

    const [PullFromGitRepoMutation, { loading: pullLoading }] =
      usePullFromGitRepoMutation();
    const dispatch = useDispatch();
    const history = useHistory();

    const pullFromGithub = useCallback(
      (branch: string) => {
        PullFromGitRepoMutation({ variables: { hexVersionId, branch } })
          .then(({ data: pullData }) => {
            if (pullData && pullData.pullFromGithub == null) {
              toaster.show({
                message: (
                  <PullError message="No new changes were found on working branch" />
                ),
                intent: Intent.WARNING,
              });
            } else if (pullData?.pullFromGithub) {
              const hasWarning = checkForWarnings(
                pullData.pullFromGithub.warnings,
              );
              // Once we've pulled down the new version, we open up the import dialog
              // to show any warnings if appropriate.
              if (hasWarning) {
                dispatch(setImportWarnings(pullData.pullFromGithub.warnings));
              }
              Routes.push(history, Routes.LOGIC, {
                hexId,
                version: pullData.pullFromGithub.hexVersion.version,
                urlParams: hasWarning
                  ? { dialog: "version-import" }
                  : undefined,
              });
            }
          })
          .catch((e: Error) => {
            toaster.show({
              message: <GenericError message={e.message} />,
              intent: Intent.DANGER,
            });
            console.error(e);
          });
      },
      [
        dispatch,
        hexId,
        hexVersionId,
        history,
        PullFromGitRepoMutation,
        toaster,
      ],
    );
    const [repoMenuOpen, setRepoMenuOpen] = useState<boolean>(false);
    const generateMenuItems = useCallback(() => {
      return [
        <HexMenuItem
          key="syncSettings"
          disabled={!projectEditable}
          text="Edit sync settings"
          // eslint-disable-next-line react/jsx-no-bind
          onClick={() => setDialogOpen(true)}
        />,
      ];
    }, [projectEditable]);

    const renderPackageCallback = useCallback(
      (repo: SyncRepoAndWorkingBranch) => {
        const menuItems = generateMenuItems();
        return renderVcsPackageTableCells({
          connectionType:
            currentRepo?.vcsConnectionType ?? VcsConnectionType.GITHUB_SYNC,
          repo,
          menuItems,
          isOpen: repoMenuOpen,
          openRepoMenu: () => setRepoMenuOpen(true),
          closeRepoMenu: () => setRepoMenuOpen(false),
        });
      },
      [currentRepo?.vcsConnectionType, generateMenuItems, repoMenuOpen],
    );
    return (
      <Container>
        <SidebarDivider />
        <SidebarHeader
          description={
            <>
              Sync your project with GitHub or GitLab.{" "}
              <DocsLink to={DOCS_LINKS.GitHubSync}>Learn more.</DocsLink>
            </>
          }
          title="Git sync"
          topActions={
            enabled ? (
              <HexTooltip
                content={tooltipContent}
                disabled={!(noRepos || error)}
                placement="bottom-start"
              >
                <HexSwitch
                  checked={syncOn}
                  disabled={noRepos || loading || !!error || !canEdit}
                  onChange={toggleSyncCallback}
                />
              </HexTooltip>
            ) : (
              <FeatureGateToolTip content={<>enable Git sync</>}>
                <HexSwitch
                  checked={false}
                  disabled={true}
                  onChange={undefined}
                />
              </FeatureGateToolTip>
            )
          }
        />
        <Collapse isOpen={syncOn && !loading && enabled}>
          {dialogOpen && currentRepo && (
            <SyncSettingsDialog
              closeDialog={closeDialog}
              currentRepo={currentRepo}
              dialogData={formData}
              dialogOpen={dialogOpen}
              error={sameFile ? undefined : updateError}
              fileExists={fileExists}
              formRef={formRef}
              loading={formLoading}
              sameFile={sameFile}
              selectedRepo={selectedRepo}
              setSelectedRepo={setSelectedRepo}
              submitForm={submitForm}
              syncRepos={castDraft(data?.orgSyncRepos ?? [])}
              onChangeForm={onChangeForm}
            />
          )}
          {data && currentRepo && (
            <MinimalTable
              columns={tableColumns}
              data={[
                {
                  ...currentRepo,
                  workingBranch,
                  __typename: "SyncRepoAndWorkingBranch",
                },
              ]}
              dataToRows={renderPackageCallback}
              rowCSS={css`
                align-items: flex-start;
                padding-bottom: 10px;

                &:last-child {
                  padding-bottom: 0;
                }
              `}
              showHeader={false}
            />
          )}
          <ButtonContainer>
            <HexButtonWithDropdown
              $extraSmall={true}
              $subtle={true}
              buttonContent="Pull from working branch"
              css={css`
                height: 24px;
              `}
              disabled={!projectEditable || pullLoading || loading}
              fill={true}
              icon={<RefreshIcon />}
              loading={pullLoading}
              popoverContent={
                <HexMenu>
                  <HexMenuItem
                    text={`Pull from ${currentRepo?.mainBranch}`}
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={() =>
                      pullFromGithub(currentRepo?.mainBranch ?? "")
                    }
                  />
                </HexMenu>
              }
              // eslint-disable-next-line react/jsx-no-bind
              onClick={() => pullFromGithub(workingBranch)}
            />
          </ButtonContainer>
          <ButtonContainer>
            <PullRequestButton
              $extraSmall={true}
              $subtle={true}
              disabled={!projectEditable || loading}
              fill={true}
              icon={<GitPullIcon />}
              onClick={pullRequestCallback}
            >
              Compare on{" "}
              {currentRepo?.vcsConnectionType
                ? vcsTypeToDisplayName(currentRepo?.vcsConnectionType)
                : "remote repository"}
            </PullRequestButton>
          </ButtonContainer>
        </Collapse>
      </Container>
    );
  },
);
